// @flow
import { handleActions } from "redux-actions";
import { APP_NAMESPACE } from "shared/constants/application";
import {
  ERROR,
  LOADED,
  LOADING,
  NONE,
  PENDING,
  SUCCESS,
} from "shared/constants/status";
import { LOGOUT } from "redux/modules/auth";
import { GET_ME_REQUEST } from "redux/modules/me/meActions";
import {
  REQUEST as CHAPTER_LOCATIONS_REQUEST,
  SELECT as SELECT_CHAPTER,
} from "redux/modules/instructorChapters/instructorChapters";
import type { EventContent } from "redux/api-types/EventContent";
import type { EventTemplate } from "redux/api-types/EventTemplate";
import type { ChapterEventFormValues } from "./typedAddress";
import {
  parseEventContent,
  parseTemplates,
} from "redux/modules/createCabelasClass/parsers/eventLookups";

const namespace = `${APP_NAMESPACE}/createChapterEvents`;
export const CONTENT = `${namespace}/CONTENT/REQUEST`;
export const CONTENT_SUCCESS = `${namespace}/CONTENT/SUCCESS`;
export const CONTENT_ERROR = `${namespace}/CONTENT/ERROR`;
export const LOOKUPS = `${namespace}/LOOKUPS/REQUEST`;
export const LOOKUPS_SUCCESS = `${namespace}/LOOKUPS/SUCCESS`;
export const LOOKUPS_ERROR = `${namespace}/LOOKUPS/ERROR`;
export const TIMEZONE = `${namespace}/TIMEZONE/REQUEST`;
export const TIMEZONE_SUCCESS = `${namespace}/TIMEZONE/SUCCESS`;
export const TIMEZONE_ERROR = `${namespace}/TIMEZONE/ERROR`;
export const SUBMIT = `${namespace}/POST/REQUEST`;
export const PROGRESS = `${namespace}/POST/PROGRESS`;
export const SUBMIT_SUCCESS = `${namespace}/POST/SUCCESS`;
export const SUBMIT_ERROR = `${namespace}/POST/ERROR`;
export const CLEAR = `${namespace}/CLEAR`;
export const RESET_ACTIONS: Array<string> = [
  CLEAR,
  CHAPTER_LOCATIONS_REQUEST,
  GET_ME_REQUEST,
  LOGOUT,
];
export type State = {
  contentStatus: string,
  contentError: string,
  contentRequestId: number,
  lookupStatus: string,
  lookupError: string,
  locationId: string,
  requestId: number,
  eventContent: { [string]: EventContent },
  templates: { [string]: EventTemplate },
  timezoneStatus: string,
  timezoneError: string,
  timezoneRequestId: number,
  postStatus: string,
  postError: string,
  postRequestId: number,
  needsReview: boolean,
  progress: { created: number, total: number },
};
export type Action = {
  type: string,
  payload: {
    requestId: number,
    locationId?: string,
    values?: ChapterEventFormValues,
    content?: Array<EventContent>,
    templates?: Array<EventTemplate>,
    error?: string,
    created?: number,
    total?: number,
    needsReview?: boolean,
  },
};
let sequence = 0;
export const getChapterEventContent = (): Action => ({
  type: CONTENT,
  payload: { requestId: ++sequence },
});
export const getChapterSchedulingLookups = (locationId: string): Action => ({
  type: LOOKUPS,
  payload: { locationId, requestId: ++sequence },
});
export const lookupChapterTimezone = (
  location: ?{ locationId: string }
): Action => ({
  type: TIMEZONE,
  payload: { locationId: location?.locationId || "", requestId: ++sequence },
});
export const postChapterEvents = (values: ChapterEventFormValues): Action => ({
  type: SUBMIT,
  payload: { values, requestId: ++sequence },
});
export const clearChapterScheduling = (): mixed => ({ type: CLEAR });
const initialState: State = {
  contentStatus: NONE,
  contentError: "",
  contentRequestId: 0,
  lookupStatus: NONE,
  lookupError: "",
  locationId: "",
  requestId: 0,
  eventContent: {},
  templates: {},
  timezoneStatus: NONE,
  timezoneError: "",
  timezoneRequestId: 0,
  postStatus: NONE,
  postError: "",
  postRequestId: 0,
  needsReview: false,
  progress: { created: 0, total: 0 },
};
const isLookup = (state: State, action: Action): boolean =>
  state.lookupStatus === LOADING &&
  state.requestId === action.payload.requestId;
const isTimezone = (state: State, action: Action): boolean =>
  state.timezoneStatus === LOADING &&
  state.timezoneRequestId === action.payload.requestId;
const isPost = (state: State, action: Action): boolean =>
  state.postStatus === PENDING &&
  state.postRequestId === action.payload.requestId;
const resets = RESET_ACTIONS.reduce(
  (
    handlers: { [string]: () => State },
    action: string
  ): ({ [string]: () => State }) => ({
    ...handlers,
    [action]: (): State => initialState,
  }),
  {}
);
export default handleActions(
  {
    ...resets,
    [CONTENT]: (state: State, { payload }: Action): State => ({
      ...state,
      contentStatus: LOADING,
      contentError: "",
      contentRequestId: payload.requestId,
      eventContent: {},
    }),
    [CONTENT_SUCCESS]: (state: State, action: Action): State =>
      state.contentStatus === LOADING &&
      state.contentRequestId === action.payload.requestId
        ? {
            ...state,
            contentStatus: LOADED,
            eventContent: parseEventContent(action.payload.content),
          }
        : state,
    [CONTENT_ERROR]: (state: State, action: Action): State =>
      state.contentStatus === LOADING &&
      state.contentRequestId === action.payload.requestId
        ? {
            ...state,
            contentStatus: ERROR,
            contentError:
              action.payload.error || "Unable to load Chapter content.",
          }
        : state,
    [SELECT_CHAPTER]: (state: State): State => ({
      ...initialState,
      contentStatus: state.contentStatus,
      contentError: state.contentError,
      contentRequestId: state.contentRequestId,
      eventContent: state.eventContent,
    }),
    [LOOKUPS]: (state: State, { payload }: Action): State => ({
      ...state,
      templates: {},
      lookupError: "",
      lookupStatus: LOADING,
      locationId: payload.locationId || "",
      requestId: payload.requestId,
    }),
    [LOOKUPS_SUCCESS]: (state: State, action: Action): State =>
      isLookup(state, action)
        ? {
            ...state,
            lookupStatus: LOADED,
            templates: parseTemplates(action.payload.templates),
          }
        : state,
    [LOOKUPS_ERROR]: (state: State, action: Action): State =>
      isLookup(state, action)
        ? {
            ...state,
            lookupStatus: ERROR,
            lookupError:
              action.payload.error || "Unable to load scheduling options.",
          }
        : state,
    [TIMEZONE]: (state: State, { payload }: Action): State => ({
      ...state,
      timezoneStatus: payload.locationId ? LOADING : NONE,
      timezoneError: "",
      timezoneRequestId: payload.requestId,
    }),
    [TIMEZONE_SUCCESS]: (state: State, action: Action): State =>
      isTimezone(state, action) ? { ...state, timezoneStatus: LOADED } : state,
    [TIMEZONE_ERROR]: (state: State, action: Action): State =>
      isTimezone(state, action)
        ? {
            ...state,
            timezoneStatus: ERROR,
            timezoneError:
              action.payload.error || "Choose the venue time zone manually.",
          }
        : state,
    [SUBMIT]: (state: State, { payload }: Action): State =>
      state.postStatus === PENDING ||
      state.postStatus === SUCCESS ||
      state.needsReview
        ? state
        : {
            ...state,
            postStatus: PENDING,
            postRequestId: payload.requestId,
            postError: "",
            progress: { created: 0, total: 0 },
          },
    [PROGRESS]: (state: State, action: Action): State =>
      isPost(state, action)
        ? {
            ...state,
            progress: {
              created: action.payload.created || 0,
              total: action.payload.total || 0,
            },
          }
        : state,
    [SUBMIT_SUCCESS]: (state: State, action: Action): State =>
      isPost(state, action) ? { ...state, postStatus: SUCCESS } : state,
    [SUBMIT_ERROR]: (state: State, action: Action): State =>
      isPost(state, action)
        ? {
            ...state,
            postStatus: ERROR,
            postError: action.payload.error || "Unable to schedule events.",
            needsReview: !!action.payload.needsReview,
          }
        : state,
  },
  initialState
);
